import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { Button, Dropdown } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { ModStatus } from '@/api/generated/schemas';
import { PublicationModStatusModal } from './PublicationModStatusModal';

interface PublicationBulkModStatusControlProps {
  /** User-catalog publication ids. */
  ids: number[];
  /** User-catalog product (format) ids the action is scoped to. */
  productIds?: number[];
  /**
   * A run finished; data should be refetched. The modal stays open.
   * Pass `onClosed` to clear the table selection; clearing it in `onDone`
   * would unmount this control and the open progress modal.
   */
  onDone?: () => void;
  /**
   * The user closed the modal after a finished run — the safe point to
   * clear the table selection (see `onDone`).
   */
  onClosed?: () => void;
  /**
   * The modal opened or closed. Callers that render this control behind a
   * "something is selected" condition must keep it mounted while open: the
   * refetch after each step can drop the acted-on rows out of the view.
   */
  onOpenChange?: (open: boolean) => void;
}

/** `BulkModStatusControl` twin for publications (see the hook for why). */
export const PublicationBulkModStatusControl = ({
  ids,
  productIds,
  onDone,
  onClosed,
  onOpenChange,
}: PublicationBulkModStatusControlProps) => {
  const t = useTranslate();
  // Every status goes through the modal: it shows the publications and
  // their formats, and for "enabled" the whole blocking tree.
  const [target, setTarget] = useState<ModStatus | null>(null);
  const [completed, setCompleted] = useState(false);
  // The run acts on what was selected when it started. Each finished step
  // invalidates the caller's list, and the refetched rows may no longer
  // include the objects just changed — a live `ids` would empty mid-run.
  const [run, setRun] = useState<{
    ids: number[];
    productIds?: number[];
  } | null>(null);

  const openFor = (status: ModStatus) => {
    setCompleted(false);
    setRun({ ids, productIds });
    setTarget(status);
    onOpenChange?.(true);
  };
  const close = () => {
    setTarget(null);
    setRun(null);
    onOpenChange?.(false);
    // Clearing the selection unmounts the toolbar (and this modal), so it
    // must wait until the user dismisses the progress view.
    if (completed) onClosed?.();
  };

  return (
    <>
      {target && run && (
        <PublicationModStatusModal
          ids={run.ids}
          productIds={run.productIds}
          status={target}
          open
          onClose={close}
          onStepDone={onDone}
          onDone={() => {
            setCompleted(true);
            onDone?.();
          }}
        />
      )}
      <Dropdown
        trigger={['click']}
        disabled={ids.length === 0}
        menu={{
          items: Object.values(ModStatus).map((value) => ({
            key: value,
            label: t(`base.mod_status.${value}`),
          })),
          onClick: ({ key }) => openFor(key as ModStatus),
        }}
      >
        <Button type="primary">
          {t('user_catalog.common.bulk_mod_status.apply')} <DownOutlined />
        </Button>
      </Dropdown>
    </>
  );
};
